import { QRCode } from '@getstep/qrcode-dom'
import classNames from 'classnames'
import type { FunctionComponent } from 'react'
import { useState } from 'react'

import styles from './share-qr-code.module.scss'
import { Avatar } from '@components/avatar'
import { Button } from '@components/button/button'
import { Heading } from '@components/heading'
import { Copy } from '@components/icons/copy'
import { QRCode as QRCodeIcon } from '@components/icons/qrcode'
import { ModalContainer } from '@components/modal/modal'
import { PublicProfilePageStore } from '@lib/store/PublicProfilePageStore'
import { snackbarStore } from '@lib/store/ui/snackbar'
import { copyToClipboard } from '@lib/utils/clipboard'
import { TEXT } from '@lib/utils/text'
import { LINK } from '@shared/util/link'
import { controlWidthMedium, iconSizeSmall } from '@styles/variables'

const size = controlWidthMedium || '250px'
const qrCodeProps = (url: string) =>
    PublicProfilePageStore.generateQRCodeOptions(parseInt(size), 'blue', url)

/**
 * Displays an icon that opens up a share dialog on activation.
 */
export const ShareQRCode: FunctionComponent<React.PropsWithChildren<Props>> = ({
    username,
    pictureUrl,
    name,
    className,
}) => {
    const [showDialog, setShowDialog] = useState(false)

    const link = (medium?: string) =>
        LINK.profile(username)
            .addQueryParams(medium ? { utm_medium: medium } : {})
            .serialize(undefined, { canonical: true })

    const preview = link().replace('https://', '')

    return (
        <>
            <Button
                data-testid='qrcode.shareButton'
                animatePress={false}
                size='medium'
                preset='plain'
                className={classNames(styles.button, className)}
                onClick={() => {
                    setShowDialog(true)
                }}
            >
                <QRCodeIcon className={styles.icon} />
            </Button>

            <ModalContainer
                visible={showDialog}
                onHide={() => setShowDialog(false)}
            >
                <div className={styles.container}>
                    <div
                        data-testid='qrcode.image'
                        className={styles['qrcode-container']}
                    >
                        <QRCode
                            {...qrCodeProps(link('payme-qr'))}
                            backgroundColor='transparent'
                            quietZone={0}
                            className={styles.qrcode}
                        />
                    </div>
                    <div className={styles.avatar}>
                        <Avatar
                            size='large'
                            border={false}
                            name={name}
                            source={pictureUrl}
                        />
                    </div>
                </div>

                <div className={styles.footer}>
                    <Heading level={1} className={styles.name}>
                        {name}
                    </Heading>

                    <div className={styles.text}>{TEXT.scanToPay}</div>

                    <Button
                        animatePress={false}
                        size='medium'
                        className={styles.link}
                        preset='anchor'
                        onClick={async () => {
                            await copyToClipboard(link())
                            snackbarStore.show(TEXT.linkCopied, 'link.copied')
                        }}
                    >
                        {preview}
                        <Copy className={styles.copy} height={iconSizeSmall} />
                    </Button>
                </div>
            </ModalContainer>
        </>
    )
}

interface Props {
    /**
     * Extra className for positioning and stuff.
     */
    className?: string

    /**
     * Username to show in modal
     */
    username: string

    /**
     * Person's actual name.
     */
    name?: string

    /**
     * Avatar source.
     */
    pictureUrl?: string
}
